import React, { useEffect, useState } from 'react';
import UiAlert from '../../../ui/alert/UiAlert';
import Wrapper from './styles';
import { stores, useStoreWithSelector } from '../../../../stores';
import { translate } from '../../../../services/translate';
import SportTicketDetails from '../../ticket/details/SportTicketDetails';
import { getTicketDetailsByTickedId } from '../../../../microservices/sbgate';
import { logger } from '../../../../services/logger';
import { isMobile } from '../../../../services/browser';
import SportBetslipRecommendation from '../recommendation/SportBetslipRecommendation';
import { postBettingContext } from '../../../../microservices/analytics';
import UiButton from '../../../ui/button/UiButton';
import { TicketDetails } from '../../../../services/sports/types';
import { isFeatureAvailable } from '../../../../services/feature';
import { DATE_YEAR_TIME_FORMAT, getFormattedDate } from '../../../../services/date';
import { formattedAmountWithCurrency } from '../../../../services/currency';
import { useStore } from '../../../../hooks/useStore';
import { FEATURE } from '../../../../services/types';

interface Props {
    marketId: string;
}

function SportBetslipReceipt({ marketId }: Props) {
    const BETTING_CONTEXTS = {
        UPSELL: 'upsell',
    };
    const [ticketId] = useStoreWithSelector(stores.sports.betSlipPlacingState, (state) => state.receiptById[marketId]);
    const [ticketResponse, setTicketResponse] = useState<TicketDetails>();
    const [upsellRecommendation] = useStore(stores.marketing.upsellRecommendation);
    const [isAuthenticated] = useStore(stores.isAuthenticated);

    useEffect(() => {
        if (ticketId) {
            loadTicketDetails(ticketId);
        }
    }, [ticketId]);

    useEffect(() => {
        if (isAuthenticated) {
            return;
        }
        removeReceipt();
    }, [isAuthenticated]);

    async function loadTicketDetails(ticketId: string) {
        try {
            const response = await getTicketDetailsByTickedId(ticketId);
            sendRecommendedTicketTracking(response);
            setTicketResponse(response);
        } catch (error) {
            logger.error('SportBetslipReceipt', 'loadTicketDetails', error);
        }
    }

    function sendRecommendedTicketTracking(ticketDetails: TicketDetails) {
        const { marketId, matchId } = upsellRecommendation;
        ticketDetails.uniqueSelections.forEach((selection) => {
            if (selection.market_id === marketId && selection.match_id === matchId) {
                isFeatureAvailable(FEATURE.TICKET_TRACKING) &&
                    postBettingContext({ ticket_id: ticketDetails.ticket.id, context: BETTING_CONTEXTS.UPSELL });
                stores.marketing.upsellRecommendation.set((state) => ({
                    ...state,
                    marketId: undefined,
                    matchId: undefined,
                }));
            }
        });
    }

    function removeReceipt() {
        stores.sports.betSlipPlacingState.set((prevState) => {
            const { [marketId]: _, ...updatedReceiptById } = prevState.receiptById;
            return {
                ...prevState,
                receiptById: updatedReceiptById,
            };
        });

        if (isMobile()) {
            stores.isBetslipOpen.set(false);
        }
    }

    if (!ticketResponse) {
        return null;
    }

    const isManualAcceptance = ticketResponse.ticket.ma_status;
    return (
        <Wrapper>
            <UiButton block onClick={removeReceipt} className="remove-receipt">
                {translate('Close', 'ui.common')}
            </UiButton>

            {isManualAcceptance && (
                <UiAlert warning>
                    {translate('Your bet request is sent to Manual Acceptance!', 'ui.betslip')}{' '}
                    {translate('Stake', 'ui.betslip')}{' '}
                    {formattedAmountWithCurrency(ticketResponse.ticket.ma_stake_requested)}.
                </UiAlert>
            )}

            <p>
                {translate('Bet ID:', 'ui.betslip')} {ticketResponse.ticket.display_id} /{' '}
                <span>
                    {getFormattedDate({
                        date: ticketResponse.ticket.created_at,
                        format: DATE_YEAR_TIME_FORMAT,
                        useDots: true,
                    })}
                </span>
            </p>

            <>
                {Boolean(ticketResponse.ticket.total_stake) && (
                    <UiAlert center success>
                        {translate('Your bet is placed. Good luck!', 'ui.betslip')}
                    </UiAlert>
                )}

                <div className="betslip-receipt">
                    <SportTicketDetails ticket={ticketResponse} isCompactView onTicketClosed={removeReceipt} />
                </div>

                {!isNaN(+marketId) && <SportBetslipRecommendation marketId={marketId as number | 'null'} />}
            </>
        </Wrapper>
    );
}

export default SportBetslipReceipt;
